import { debounce } from 'lodash';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import FuelTags from '@/components/fuel-tags';
import VNAutocomplete from '@/components/VNAutocomplete/VNAutocomplete';
import VNCheckBox from '@/components/VNCheckBox/VNCHeckBox';
import VNDatePicker from '@/components/VNDatePicker/VNDatePicker';
import VNTextField from '@/components/VNTextField/VNTextField';
import { AssetSource } from '@/enums';
import type { AccessSchedule } from '@/models/channel';
import { applicationDataSelector } from '@/store/slices/application-data';
import { accessUtils } from '@/utils/accessUtils';
import clientUtils from '@/utils/clientUtils';
import itemUtils from '@/utils/itemUtils';

import Metadata from './Metadata';

interface Props {
  isMultiple?: boolean;
  hasMov?: boolean;
  isNew?: boolean;
}

function VideoForm({ isMultiple, hasMov = false, isNew = false }: Props) {
  const [filteredCategories, setFilteredCategories] = useState([]); // need to filter out deleted and disabled categories
  const [allowMultiCategorySelect, setAllowMultiCategorySelect] = useState(true);
  const { ouData } = useSelector(applicationDataSelector);
  const { getValues, setValue, watch } = useFormContext();
  const [originalValues, setOriginalValue] = useState(getValues());

  const [previousTags, setPreviousTags] = useState(getValues('tags') || []);
  // const [previousCommon, setPreviousCommon] = useState(itemUtils.getFullTagsFromCommonMetadata(getValues('common')) || []);

  const tags = watch('tags');
  const common = watch('common');
  const commonCategories = watch('common.categories');

  useEffect(() => {
    // Set allowMultipleCategories
    const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
    const itemCategories = getValues('categories');
    if (allowMultipleCategories === true || itemCategories?.length > 1) {
      setAllowMultiCategorySelect(true);
    } else {
      setAllowMultiCategorySelect(false);
    }

    // Set categories
    const myFilteredCategories = itemUtils.getFilteredCategories(ouData.categories, itemCategories);
    setFilteredCategories(myFilteredCategories);
  }, [ouData]);

  // When the categories is a single select then it just writes a string as the value, and
  // this code will transform it back to an array so its always an array
  useEffect(() => {
    // console.log('commonCategories changed', { commonCategories });
    if (_.isString(commonCategories)) {
      setValue('common.categories', [commonCategories]);
    }
  }, [commonCategories]);

  const onEmbargoDateChanged = (oldEmbargo: string, newEmbargo: string) => {
    // console.log('onEmbargoDateChanged', { newEmbargo, newEmbargo });
    let newAccessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules'));
    if (newAccessSchedules == null) {
      newAccessSchedules = [];
    }
    newAccessSchedules = accessUtils.ensureEmbargoSet(newAccessSchedules, oldEmbargo, newEmbargo);
    setValue('accessSettings.temporal.accessSchedules', newAccessSchedules);
  };

  const onExpireDateChanged = (oldExpire: string, newExpire: string) => {
    // console.log('onExpireDateChanged', { oldExpire, newExpire });
    let newAccessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules'));
    if (newAccessSchedules == null) {
      newAccessSchedules = [];
    }
    newAccessSchedules = accessUtils.ensureExpireSet(newAccessSchedules, oldExpire, newExpire);
    setValue('accessSettings.temporal.accessSchedules', newAccessSchedules);
  };

  function compareTags(current: string[], previous: string[]) {
    let direction = 0;
    let diff = [] as string[];
    if (current.length < previous.length) {
      direction = -1;
      diff = previous.filter((tag) => !current.includes(tag));
    } else if (current.length > previous.length) {
      direction = 1;
      diff = current.filter((tag) => !previous.includes(tag));
    }

    return { diff, direction };
  }

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      const tagChange = compareTags(tags, previousTags);

      if (tagChange.direction > 0 || tagChange.direction < 0) {
        setPreviousTags(tags);
        const commonFromTags = itemUtils.extractCommonTags(tags);
        const mergedCommon = {
          ...commonFromTags,
          categories: common?.categories,
        };
        setValue('common', mergedCommon);
      }
    }, 300);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [tags]);

  const debouncedUpdateRef = useRef<any>(null);

  useEffect(() => {
    // Create a debounced function
    debouncedUpdateRef.current = debounce((commonData) => {
      const commonTagsArray = itemUtils.getFullTagsFromCommonMetadata(commonData);
      const extractedTagsObj = itemUtils.extractCommonTags(tags);
      const basicTags = extractedTagsObj.tags;
      const extractedTags = [] as string[];
      Object.keys(extractedTagsObj).forEach((key) => {
        if (key !== 'tags') {
          extractedTags.push(...extractedTagsObj[key]);
        }
      });

      const commonTagsChange = compareTags(commonTagsArray, extractedTags);
      if (commonTagsChange.direction > 0 || commonTagsChange.direction < 0) {
        const newTags = Array.from(new Set([...basicTags, ...commonTagsArray]));
        setValue('tags', newTags);
        setPreviousTags(newTags);
      }
    }, 300);

    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('common.')) {
        const commonData = value.common;
        if (debouncedUpdateRef.current) {
          debouncedUpdateRef.current(commonData);
        }
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedUpdateRef.current?.cancel?.();
    };
  }, [common]);

  return (
    <div className="form">
      <div className="form-grid" style={{ marginBottom: '1em' }}>
        <VNTextField name="schemaType" label="Media Type" disabled />
        <VNAutocomplete
          name="common.categories"
          label="Categories"
          multiple={allowMultiCategorySelect}
          allowClear
          options={filteredCategories}
          labelProp="name"
          valueProp="id"
        />
        {isMultiple ? (
          <VNTextField name="title" label="Title (populated with filename)" disabled />
        ) : (
          <VNTextField name="title" label="Title" rules={{ required: 'Title is required' }} required />
        )}
        <FuelTags id="series-tags" inputTagWidth={300} tags={watch('tags') ?? []} onChange={(value) => setValue('tags', value)} />
        <VNDatePicker
          name="publicationDate"
          label={`Published (${ouData.timezoneAbbreviation})`}
          timezone={ouData.timezone}
          rules={{ required: 'Published Date is required' }}
          required
        />

        <div className="sub-row">
          {getValues('source') !== AssetSource.ExternalClip && (
            <VNCheckBox name="applyAutomatedTransitions" label="Include Automated Transitions" />
          )}
          <VNCheckBox name="doNotDelete" label="Retain/Do Not Delete" />
        </div>

        <VNTextField name="synopsis" label="Description" rows={4} disabled={isMultiple} />

        {getValues('source') === AssetSource.ExternalClip && (
          <VNTextField
            name="externalHlsFileUrl"
            label="External HLS URL"
            required
            rules={{
              required: 'External HLS URL is required',
              validate: (value) => clientUtils.isValidM3u8Url(value) || 'Invalid External HLS URL',
            }}
            disabled={!isNew}
          />
        )}

        {getValues('source') !== AssetSource.ExternalClip && (
          <div className="control-group">
            <div className="sub-row">
              <VNDatePicker
                name="embargoDate"
                label={`Embargo (${ouData.timezoneAbbreviation})`}
                timezone={ouData.timezone}
                onDateChanged={onEmbargoDateChanged}
              />
              <VNDatePicker
                name="expireDate"
                label={`Expires (${ouData.timezoneAbbreviation})`}
                timezone={ouData.timezone}
                onDateChanged={onExpireDateChanged}
              />
            </div>
            {hasMov && (
              <div className="mov-checkbox">
                <VNCheckBox name="includeAsTransition" label="Include .mov(s) as Transition(s)" />
              </div>
            )}
          </div>
        )}
      </div>
      <Metadata isMultiple={isMultiple} isNew={isNew} />
    </div>
  );
}

export default VideoForm;
